import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useBlocker } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { useMutation } from '@tanstack/react-query';
import Client from '../../models/Client';
import { Helmet } from 'react-helmet';

// Step Components (to be created)
import ImportSummaryStep from './components/import-wizard/ImportSummaryStep';
import ImportInfoStep from './components/import-wizard/ImportInfoStep';
import ImportBrandsStep from './components/import-wizard/ImportBrandsStep';
import ImportWorkGalleryStep from './components/import-wizard/ImportWorkGalleryStep';
import ImportProjectHighlightsStep from './components/import-wizard/ImportProjectHighlightsStep';
import ImportPartnerProgramsStep from './components/import-wizard/ImportPartnerProgramsStep';
import ImportIndustriesStep from './components/import-wizard/ImportIndustriesStep';
import ImportAwardsCertificationsStep from './components/import-wizard/ImportAwardsCertificationsStep';
import ImportServiceOfferingsStep from './components/import-wizard/ImportServiceOfferingsStep';
import ImportReviewStep from './components/import-wizard/ImportReviewStep';

const FALLBACK_SERVICE_OFFERING_PATH = '/images/service_offering.png';

const supplierUpdateRequest = async (clientId, params) => {
    let displayIndexCheck = [
        'brands',
        'industries_worked',
        'assets',
        'service_offerings',
        'key_clients',
        'partnerable_programs',
    ]

    let updateParams = TB.cloneDeep(params);

    TB.map(displayIndexCheck, prop => {
        if (updateParams[prop]?.length) {
            updateParams[prop] = TB.orderBy(updateParams[prop], ['display_index', item => TB.isDefined('display_index')], ['asc', 'desc']);
            updateParams[prop] = TB.map(updateParams[prop], (item, index) => {
                return {
                    ...item,
                    display_index: index + 1
                }
            })
        }
    })

    if (updateParams.locations?.length) {
        updateParams.locations = [updateParams.location, ...updateParams.locations];
    } else if (updateParams.location) {
        updateParams.locations = [updateParams.location];
    }

    if (updateParams.service_offerings?.length) {
        updateParams.service_offerings = updateParams.service_offerings.map(so => {
            return {
                ...so,
                image_path: so.image_path || FALLBACK_SERVICE_OFFERING_PATH,
            }
        })
    }

    updateParams.generated_account_data = null;

    console.log('supplierUpdateRequest', updateParams)
    TB.auth().SupplierUpdateRequest = TB.request(
        `/api/clients/${clientId}/update`,
        updateParams
    ).headers({
        'Content-Type': 'multipart/form-data'
    });

    let response = await TB.auth().SupplierUpdateRequest.post().catch(e => {
        console.error('ERROR: supplierUpdate', e);
    })

    if (!response || !TB.has(response, 'success.data.client') || !TB.has(response, 'success.data.auth')) {
        TB.auth().SupplierUpdateRequest.didHandleError = await TB.auth().SupplierUpdateRequest.handleError();
        if (TB.auth().SupplierUpdateRequest.didHandleError
            && TB.auth().SupplierUpdateRequest.shouldRerun) {
            return supplierUpdateRequest(clientId, params);
        }

        throw new Error("Something went wrong");
    }

    return response
}

const SupplierImportProfileWizard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth, update: updateAuth } = useAuth();

    const [supplierOrg, setSupplierOrg] = useState(() => {
        return Client?.make(TB.cloneDeep({ ...auth.supplierOrg() }));
    });

    const [isDisabled, setIsDisabled] = useState(false);
    const [isImportComplete, setIsImportComplete] = useState(false);

    const accountData = useMemo(() => {
        let data = TB.cloneDeep(supplierOrg?.generated_account_data || {});
        if (data.profile_links) {
            let links = TB.reduce(laravel.talent_profile_link_types || {}, (result, link, linkId) => {
                result.push({...link, value: linkId})
                return result
            }, []);
            data.profile_links = TB.map(data.profile_links, (link) => {
                link.type = TB.find(links, ['name', link.type])?.value;
                return link;
            });
            let website = TB.find(links, ['name', 'Website']);

            data.profile_links = TB.filter(data.profile_links, (link) => {
                return !TB.isNaN(parseInt(link.type)) && link.type !== website.value;
            })
        }
        return data;
    }, [supplierOrg]);

    // Initialize selectedFields with all items selected by default
    const resetSelected = (data) => {
        const arrayFields = [
            'assets',
            'awards',
            'brands',
            'certifications',
            'industries_worked',
            'key_clients',
            'locations',
            'partner_programs',
            'profile_links',
            'service_offerings',
        ];

        const singleFields = [
            'about',
            'website',
        ];

        const booleanFields = [
            'is_black_owned',
            'is_woman_owned',
        ];

        let selected = {};

        // Handle array fields
        TB.map(arrayFields, field => {
            if (data?.[field]?.length > 0) {
                selected[field] = {};
                TB.map(data[field], (item, index) => {
                    selected[field][index] = true;
                });
            }
        });

        // Handle single value fields
        TB.map(singleFields, field => {
            if (data?.[field]) {
                selected[field] = true;
            }
        });

        // Handle boolean fields (only select if true)
        TB.map(booleanFields, field => {
            if (data?.[field] === true) {
                selected[field] = true;
            }
        });

        return selected;
    };

    const [selectedFields, setSelectedFields] = useState(() => resetSelected(accountData));

    const updateSupplierManageProfileMutation = useMutation(
        ['supplierProfileUpdate', supplierOrg.id],
        params => supplierUpdateRequest(supplierOrg.id, params)
    )

    // Define wizard steps with their metadata
    const wizardSteps = useMemo(() => {
        const steps = [
            {
                path: 'summary',
                label: 'Summary',
                icon: 'fas fa-info-circle',
                alwaysShow: true
            }
        ];

        // Only add steps if there's data for that category
        if (accountData?.about || accountData?.website || accountData?.is_black_owned || accountData?.is_woman_owned || accountData?.profile_links?.length || accountData?.locations?.length) {
            steps.push({
                path: 'info',
                label: 'Info',
                icon: 'fas fa-info-circle',
                fields: ['about', 'website', 'is_black_owned', 'is_woman_owned', 'profile_links', 'locations']
            });
        }

        if (accountData?.brands?.length) {
            steps.push({
                path: 'brands',
                label: 'Brands',
                icon: 'fas fa-handshake',
                fields: ['brands']
            });
        }

        if (accountData?.assets?.length) {
            steps.push({
                path: 'work-gallery',
                label: 'Work Gallery',
                icon: 'fas fa-images',
                fields: ['assets']
            });
        }

        if (accountData?.key_clients?.length) {
            steps.push({
                path: 'project-highlights',
                label: 'Project Highlights',
                icon: 'fas fa-briefcase',
                fields: ['key_clients']
            });
        }

        if (accountData?.partner_programs?.length || accountData?.partnerable_programs?.length) {
            steps.push({
                path: 'partner-programs',
                label: 'Partner Programs',
                icon: 'fas fa-certificate',
                fields: ['partner_programs', 'partnerable_programs']
            });
        }

        if (accountData?.industries_worked?.length) {
            steps.push({
                path: 'industries',
                label: 'Industries',
                icon: 'fas fa-building',
                fields: ['industries_worked']
            });
        }

        if (accountData?.awards?.length || accountData?.certifications?.length) {
            steps.push({
                path: 'awards-certifications',
                label: 'Awards & Certifications',
                icon: 'fas fa-trophy',
                fields: ['awards', 'certifications']
            });
        }

        if (accountData?.service_offerings?.length) {
            steps.push({
                path: 'service-offerings',
                label: 'Service Offerings',
                icon: 'fas fa-cogs',
                fields: ['service_offerings']
            });
        }

        // Always add review step at the end
        steps.push({
            path: 'review',
            label: 'Review',
            icon: 'fas fa-check-circle',
            alwaysShow: true
        });

        return steps;
    }, [accountData]);

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(wizardSteps, (step) => {
            return location.pathname.includes(step.path)
        })
    }, [location?.pathname, wizardSteps]);

    const currentStep = useMemo(() => {
        return wizardSteps[currentStepIndex]
    }, [currentStepIndex, wizardSteps]);

    const nextStep = useMemo(() => {
        if (currentStepIndex !== -1 && currentStepIndex < wizardSteps.length - 1) {
            return wizardSteps[currentStepIndex + 1]
        }
        return null;
    }, [currentStepIndex, wizardSteps]);

    const prevStep = useMemo(() => {
        if (currentStepIndex > 0) {
            return wizardSteps[currentStepIndex - 1]
        }
        return null;
    }, [currentStepIndex, wizardSteps]);

    const goToStep = (step) => {
        if (!step) return;
        navigate(step.path, { replace: false });
    }

    const handleNext = () => {
        if (nextStep) {
            goToStep(nextStep);
        }
    }

    const handlePrevious = () => {
        if (prevStep) {
            goToStep(prevStep);
        }
    }

    const handleFieldSelection = (field, value) => {
        setSelectedFields(prev => ({
            ...prev,
            [field]: value
        }));
    }

    // Warn user before leaving page with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            console.log('handleBeforeUnload', e)
            if (!isImportComplete) {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isImportComplete]);

    // Block React Router navigation away from wizard before import is complete
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => {
            // Don't block if import is complete
            if (isImportComplete) {
                return false;
            }

            // Check if leaving the wizard
            const isCurrentlyInWizard = currentLocation.pathname.includes('/supplier/import-profile');
            const isLeavingWizard = isCurrentlyInWizard && !nextLocation.pathname.includes('/supplier/import-profile');

            // Block navigation if leaving the wizard
            return isLeavingWizard;
        }
    );

    // Handle the blocker confirmation
    useEffect(() => {
        if (blocker.state === 'blocked' && !isImportComplete) {
            const confirmLeave = window.confirm(
                'You have unsaved changes in the import wizard. ' +
                'If you leave this page, your selections will be lost. ' +
                'Are you sure you want to leave?'
            );

            if (confirmLeave) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        } else if (blocker.state === 'blocked' && isImportComplete) {
            // If import is complete, allow navigation without prompting
            blocker.proceed();
        }
    }, [blocker, isImportComplete]);

    // Block browser back/forward navigation before import is complete
    useEffect(() => {
        if (isImportComplete) return;

        let confirmedNavigation = false;

        const handlePopState = (e) => {
            const newPath = window.location.pathname;
            const isLeavingWizard = !newPath.includes('/supplier/import-profile');

            // Only prompt if leaving the wizard and haven't already confirmed
            if (isLeavingWizard && !confirmedNavigation && !isImportComplete) {
                const confirmLeave = window.confirm(
                    'You have unsaved changes from the profile import. ' +
                    'If you leave this page, your selections will be lost. ' +
                    'Are you sure you want to leave?'
                );

                if (!confirmLeave) {
                    // User wants to stay - push current state back
                    window.history.pushState(null, '', location.pathname);
                } else {
                    // User confirmed they want to leave
                    confirmedNavigation = true;
                }
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isImportComplete, location.pathname]);

    const handleImport = async () => {
        if (isDisabled || updateSupplierManageProfileMutation.isLoading) return;

        const fields = {
            'about': 'about',
            'website': 'website',
            'is_black_owned': 'is_black_owned',
            'is_woman_owned': 'is_woman_owned',
            'assets': 'assets',
            'awards': 'awards',
            'brands': 'brands',
            'certifications': 'certifications',
            'industries_worked': 'industries_worked',
            'key_clients': 'key_clients',
            'locations': 'locations',
            'partner_programs': 'partner_programs',
            'profile_links': 'profile_links',
            'service_offerings': 'service_offerings',
        };

        let selected = {};

        TB.map(selectedFields, (checked, selectionType) => {
            // Handle single value fields (like 'about')
            if (checked === true && !TB.isObject(checked)) {
                selected[fields[selectionType]] = accountData[selectionType];
            }
            // Handle array fields
            else if (TB.isObject(checked)) {
                let selectedItems = [];
                TB.map(checked, (subCheck, index) => {
                    if (subCheck === true) {
                        selectedItems.push(accountData[selectionType][index]);
                    }
                });

                if (selectedItems?.length) {
                    // Merge with existing data if present
                    if (selectionType === 'brands' || selectionType === 'industries_worked') {
                        let mergeBy = selectionType === 'brands' ? 'domain' : 'id'
                        let newItems = TB.filter([...(selectedItems)], item => {
                            return !TB.find([...supplierOrg[fields[selectionType]] || []], [mergeBy, item[mergeBy]])
                        });
                        selectedItems = [...supplierOrg[fields[selectionType]] || [], ...newItems];
                        console.log('MERGED DATA', {selectionType, selectedItems, existing: [...supplierOrg[fields[selectionType]] || []]?.length, new: newItems?.length})
                    }else if (supplierOrg[fields[selectionType]]?.length) {
                        selectedItems = [...supplierOrg[fields[selectionType]] || [], ...selectedItems];
                    }

                    selected[fields[selectionType]] = selectedItems;
                }
            }
        });

        // Save the imported data
        let updates = { ...supplierOrg }
        for (let [key, value] of Object.entries(selected)) {
            updates = TB.set(updates, key, value)
        }

        await setIsDisabled(true);
        let updatedOrg;
        try {
            if (TB.keys(updates)?.length > 0) {

                let response = await updateSupplierManageProfileMutation.mutateAsync({ ...updates });

                console.log('handleImport', response);
                if (response) {
                    if (response.success.data.auth) {
                        await updateAuth({ ...response.success.data.auth });
                    }

                    updatedOrg = Client.make({ ...(response.success.data.client) });
                }
            } else {
                updatedOrg = supplierOrg;
            }

        } catch (e) {
            updatedOrg = false;
            console.warn('ERROR: handleImport', e);

            if (TB.auth().SupplierUpdateRequest.didFail &&
                (TB.auth().SupplierUpdateRequest.errorType === TB.Request.ErrorTooLong ||
                    TB.auth().SupplierUpdateRequest.errorType === TB.Request.ErrorLocal)) {

                setIsDisabled(false)

                return await TB.alert('error',
                    TB.trans('api.system_maintenance.title'),
                    TB.trans('api.system_maintenance.message', { code: 'XXX' }), [
                    {
                        title: 'Okay',
                    }
                ]
                )
            }
        }

        if (!updatedOrg) {
            console.log('handleImport', TB.auth().SupplierUpdateRequest)
            TB.showServerErrorToast();
            return setIsDisabled(false)
        }

        await setSupplierOrg(Client.make(updatedOrg));
        await setIsDisabled(false)
        await setIsImportComplete(true) // Mark import as complete
        TB.toast("Profile Imported Successfully!")

        // Navigate back to manage-profile
        navigate('/supplier/manage-profile', { replace: true });
    };

    // Redirect to summary if on index
    // useEffect(() => {
    //     if (location.pathname === '/supplier/import-profile' || location.pathname === '/supplier/import-profile/') {
    //         navigate('summary', { replace: true });
    //     }
    // }, [location.pathname, navigate]);

    // if (!currentStep) {
    //     return null;
    // }

    const routeProps = {
        accountData,
        selectedFields,
        onFieldSelection: handleFieldSelection,
        supplierOrg,
        isDisabled: isDisabled || updateSupplierManageProfileMutation.isLoading,
    };

    return (
        <>
            <Helmet>
                <title>Import Profile | Supplier | Wripple</title>
            </Helmet>

            <div className="talent-application-container supplier-application-container">
                <div className="talent-application-form-container">
                    {/* Sidebar Navigation */}
                    <div className="talent-app-nav p-0 w-full lg:w-3/12 xl:w-2/12" style={{ borderTop: '1px solid #E5E5E5' }}>
                        <h3 className="font-bold">
                            Import Profile
                        </h3>
                        {wizardSteps.map((step, index) => (
                            <button
                                key={step.path}
                                type="button"
                                className={TB.mapClass('step-title pointer-events-auto', {
                                    'isCompleted': index < currentStepIndex,
                                    'isActive': location.pathname.includes(step.path),
                                })}
                                disabled={isDisabled}
                                onClick={() => goToStep(step)}
                            >
                                <i className={TB.mapClass("w-4 flex flex-col items-center justify-center text-[#90A0B7]", {
                                    [step.icon]: true
                                })} />
                                {step.label}
                            </button>
                        ))}
                    </div>

                    {/* Main Content */}
                    <div className="w-full lg:w-9/12 xl:w-10/12">
                        <div className="talent-application px-4 md:px-8 xl:w-10/12 w-full mr-auto ml-0">
                            <Routes>
                                <Route index element={<Navigate to="summary" replace />} />
                                <Route path="summary" element={<ImportSummaryStep {...routeProps} />} />
                                {accountData?.about || accountData?.website || accountData?.is_black_owned || accountData?.is_woman_owned || accountData?.profile_links?.length || accountData?.locations?.length ? (
                                    <Route path="info" element={<ImportInfoStep {...routeProps} />} />
                                ) : null}
                                {accountData?.brands?.length ? (
                                    <Route path="brands" element={<ImportBrandsStep {...routeProps} />} />
                                ) : null}
                                {accountData?.assets?.length ? (
                                    <Route path="work-gallery" element={<ImportWorkGalleryStep {...routeProps} />} />
                                ) : null}
                                {accountData?.key_clients?.length ? (
                                    <Route path="project-highlights" element={<ImportProjectHighlightsStep {...routeProps} />} />
                                ) : null}
                                {accountData?.partner_programs?.length || accountData?.partnerable_programs?.length ? (
                                    <Route path="partner-programs" element={<ImportPartnerProgramsStep {...routeProps} />} />
                                ) : null}
                                {accountData?.industries_worked?.length ? (
                                    <Route path="industries" element={<ImportIndustriesStep {...routeProps} />} />
                                ) : null}
                                {accountData?.awards?.length || accountData?.certifications?.length ? (
                                    <Route path="awards-certifications" element={<ImportAwardsCertificationsStep {...routeProps} />} />
                                ) : null}
                                {accountData?.service_offerings?.length ? (
                                    <Route path="service-offerings" element={<ImportServiceOfferingsStep {...routeProps} />} />
                                ) : null}
                                <Route path="review" element={<ImportReviewStep {...routeProps} onImport={handleImport} />} />
                                <Route path="*" element={<Navigate to="summary" replace />}/>
                            </Routes>
                        </div>
                    </div>
                </div>

                {/* Footer Navigation */}
                <div className="talent-app-footer">
                    <div className="lg:w-9/12 xl:w-10/12 ml-auto flex flex-row justify-end gap-4 items-center">
                        {prevStep && (
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={isDisabled}
                                className="btn btn-outline-dark">
                                Previous
                            </button>
                        )}

                        {currentStep?.path !== 'review' && nextStep && (
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={isDisabled}
                                className="btn btn-primary">
                                Next
                            </button>
                        )}

                        {currentStep?.path === 'review' && (
                            <button
                                type="button"
                                onClick={handleImport}
                                disabled={isDisabled}
                                className="btn btn-primary">
                                {isDisabled ? 'Importing...' : 'Import Profile'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default SupplierImportProfileWizard;